import React, { FC } from 'react';
import { TbTrash } from 'react-icons/tb';

import { ActionIcon, Box, Button, Group, Stack, Switch, Text } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';

import { useChatChannelAccess } from '@diagram/hooks/use-chat-channel-access';
import { ChatChannelAccessService } from '@diagram/services/ChatChannelAccessService';

import { DiagramChatChannel, DiagramChatChannelCompany } from 'models';
import { TeamUserHelpers } from 'helpers/TeamUserHelpers';

import { Avatar, AvatarGroup } from 'components/avatar/Avatar';
import { ModalTitle } from 'components/modals/ModalTitle';
import { useAction } from 'hooks/use-action';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useCurrentProject } from 'hooks/use-current-project';
import { useProjectCollaborators } from 'hooks/use-project-collaborators';
import { useTeam } from 'hooks/use-team';
import { useUser } from 'hooks/use-user';

import { SidebarService } from '../services/SidebarService';
import { SidebarType } from '../state/sidebar';

const ChannelAccess: FC<
    ContextModalProps<{
        channel: DiagramChatChannel;
    }>
> = ({ context, innerProps }) => {
    const { channel } = innerProps;

    const { id: projectId, team: projectTeamId } = useCurrentProject()!;
    const { collaborators } = useProjectCollaborators(projectId);
    const { team } = useTeam(projectTeamId);

    const invite = () => {
        context.closeAll();

        SidebarService.open(SidebarType.SHARE);
    };

    return (
        <Stack gap="xs">
            <ModalTitle align="left">Channel Access</ModalTitle>
            <Stack gap="md">
                <Stack gap="xs">
                    <Box fz="sm" fw="600">
                        Team
                    </Box>
                    <AvatarGroup spacing="xs">
                        {TeamUserHelpers.fromTeam(team).map((teamUser) => (
                            <Avatar user={teamUser.user} key={teamUser.id} tooltip />
                        ))}
                    </AvatarGroup>
                </Stack>
                <Stack gap="xs">
                    <Box fz="sm" fw="600">
                        Collaborators
                    </Box>
                    {collaborators.length === 0 && (
                        <Text fz="sm" c="dimmed">
                            This project has no collaborators yet.
                        </Text>
                    )}
                    {collaborators.map((collaborator) => (
                        <ProjectCollaborator channel={channel} userId={collaborator.user} key={collaborator.user} />
                    ))}
                    <Button onClick={invite} size="xs">
                        Invite more collaborators
                    </Button>
                </Stack>
                <Companies channel={channel} />
                <Button onClick={() => context.closeAll()}>Done</Button>
            </Stack>
        </Stack>
    );
};

const ProjectCollaborator: FC<{
    channel: DiagramChatChannel;
    userId: string;
}> = ({ channel, userId }) => {
    const { collaborators } = useChatChannelAccess(channel);
    const { user } = useUser(userId);

    const collaborator = collaborators.find((row) => row.user === userId);

    const [toggle, toggling] = useAction(() =>
        collaborator
            ? ChatChannelAccessService.removeCollaborator(collaborator)
            : ChatChannelAccessService.addUser(channel, userId),
    );

    const jobTitle = user?.signup?.job || '';

    return user ? (
        <Group gap="xs" align="center">
            <Avatar user={user} size="24" />
            {user.name ?? user.email} {jobTitle && `(${jobTitle})`}
            <Switch checked={!!collaborator} onChange={toggle} disabled={toggling} size="sm" ml="auto" mr="0" />
        </Group>
    ) : null;
};

const Companies: FC<{
    channel: DiagramChatChannel;
}> = ({ channel }) => {
    const { companies } = useChatChannelAccess(channel);

    if (companies.length === 0) {
        return null;
    }

    return (
        <Stack gap="xs">
            <Box fz="sm" fw="600">
                Companies
            </Box>
            {companies.map((channelCompany) => (
                <ChannelManufacturer channelCompany={channelCompany} key={channelCompany.id} />
            ))}
        </Stack>
    );
};

const ChannelManufacturer: FC<{
    channelCompany: DiagramChatChannelCompany;
}> = ({ channelCompany }) => {
    const { company } = useCompanyProfile(channelCompany.company);

    const [remove, removing] = useAction(() => ChatChannelAccessService.removeCompany(channelCompany));

    return company ? (
        <Group gap="xs">
            <Avatar company={company} size={20} />
            <Box>{company.name}</Box>
            <ActionIcon variant="subtle" color="gray" size="sm" ml="auto" mr="0" loading={removing} onClick={remove}>
                <TbTrash />
            </ActionIcon>
        </Group>
    ) : null;
};

export { ChannelAccess };
